// src/components/modals/ChangePasswordDialog.tsx
import {
  Alert,
  Button,
  Drawer,
  Field,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Flex,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { PasswordInput } from '@/components/ui/password-input'
import { LuSave } from 'react-icons/lu'
import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { changePassword } from '@/api/settings.api'
import * as Yup from 'yup'

interface ChangePasswordProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void

  mockSubmitting?: boolean
  mockErrorMessage?: string
  mockInitialValues?: {
    currentPassword: string
    newPassword: string
    repeatPassword: string
  }
}

const schema = Yup.object({
  currentPassword: Yup.string().required('Dieses Feld ist erforderlich.'),
  newPassword: Yup.string().required('Dieses Feld ist erforderlich.'),
  repeatPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Kennwörter stimmen nicht überein.')
    .required('Dieses Feld ist erforderlich.'),
})

export function ChangePasswordDialog(props: ChangePasswordProps) {
  const { isOpen, onOpenChange, onSuccess, mockSubmitting, mockErrorMessage, mockInitialValues } =
    props

  const [submitting, setSubmitting] = useState(false)

  const formik = useFormik({
    initialValues: mockInitialValues || {
      currentPassword: '',
      newPassword: '',
      repeatPassword: '',
    },
    validationSchema: schema,
    validateOnMount: true,
    onSubmit: async values => {
      setSubmitting(true)
      try {
        await changePassword(values.currentPassword, values.newPassword)

        toast.success(
          'Bestätigungs-E-Mail wurde versandt',
          'Wir haben Ihnen eine E-Mail zur Bestätigung Ihres neuen Kennworts zugesandt.'
        )

        onOpenChange(false)
        onSuccess?.()
        formik.resetForm()
      } catch (err: any) {
        toast.error('Kennwort konnte nicht geändert werden', 'Bitte prüfen Sie Ihre Eingaben.')
        if (err?.field === 'currentPassword') {
          formik.setFieldError('currentPassword', err.message)
        } else {
          formik.setStatus(err?.message || 'Es ist ein Fehler aufgetreten.')
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (mockErrorMessage) formik.setStatus(mockErrorMessage)
  }, [mockErrorMessage])

  const isSubmitting = submitting || mockSubmitting

  function handleClose() {
    onOpenChange(false)
    formik.resetForm()
    formik.setStatus(undefined)
  }

  return (
    <Drawer.Root
      open={isOpen}
      size="xl"
      onOpenChange={e => {
        if (!e.open) handleClose()
      }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            {/* HEADER */}
            <Drawer.Header>
              <Drawer.Title>Kennwort aktualisieren</Drawer.Title>
            </Drawer.Header>

            {/* BODY */}
            <Drawer.Body>
              <fieldset disabled={isSubmitting} style={{ width: '100%' }}>
                <VStack gap={4} align="stretch">
                  <FieldRoot
                    invalid={!!formik.errors.currentPassword && !!formik.touched.currentPassword}
                  >
                    <FieldLabel>
                      Aktuelles Kennwort <Field.RequiredIndicator />
                    </FieldLabel>
                    <PasswordInput
                      name="currentPassword"
                      placeholder="********"
                      value={formik.values.currentPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FieldErrorText>
                      {formik.touched.currentPassword && (formik.errors.currentPassword as any)}
                    </FieldErrorText>
                  </FieldRoot>

                  <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                    <FieldRoot
                      flex="1"
                      invalid={!!formik.errors.newPassword && !!formik.touched.newPassword}
                    >
                      <FieldLabel>
                        Neues Kennwort <Field.RequiredIndicator />
                      </FieldLabel>
                      <PasswordInput
                        name="newPassword"
                        placeholder="********"
                        value={formik.values.newPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <FieldErrorText>
                        {formik.touched.newPassword && (formik.errors.newPassword as any)}
                      </FieldErrorText>
                    </FieldRoot>

                    <FieldRoot
                      flex="1"
                      invalid={!!formik.errors.repeatPassword && !!formik.touched.repeatPassword}
                    >
                      <FieldLabel>
                        Neues Kennwort wiederholen <Field.RequiredIndicator />
                      </FieldLabel>
                      <PasswordInput
                        name="repeatPassword"
                        placeholder="********"
                        value={formik.values.repeatPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <FieldErrorText>
                        {formik.touched.repeatPassword && (formik.errors.repeatPassword as any)}
                      </FieldErrorText>
                    </FieldRoot>
                  </Flex>

                  {formik.status && (
                    <Alert.Root status="error">
                      <Alert.Indicator />
                      <Alert.Description>{formik.status}</Alert.Description>
                    </Alert.Root>
                  )}

                  <Flex gap={2} align={{ base: 'flex-start', md: 'center' }}>
                    <Text>
                      Sie erhalten eine E-Mail, über die Sie das neue Kennwort bestätigen. Die
                      Änderung wird erst nach der Bestätigung wirksam.
                    </Text>
                  </Flex>
                </VStack>
              </fieldset>
            </Drawer.Body>

            {/* FOOTER */}
            <Drawer.Footer>
              <Button variant="ghost" onClick={handleClose}>
                Abbrechen
              </Button>

              <Button
                variant="subtle"
                colorPalette="green"
                loading={isSubmitting}
                disabled={!formik.isValid}
                onClick={() => formik.handleSubmit()}
              >
                <LuSave /> Kennwort aktualisieren
              </Button>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
